import { createApi } from '@reduxjs/toolkit/query/react';
import { RfiSubmittalsType } from '@types';
import { keepUnusedDataFor } from '../constants/appActions';
import { baseQueryWithReauth } from '../utils/prepHeaders';
import { ApiType } from '../utils/typeTransformer';

export const handleSubmittalPdfResponse = async (
  response: Response,
): Promise<Blob | { detail: string }> => {
  if (response.ok) return response.blob();

  const text = await response.text();
  try {
    const parsed = JSON.parse(text);
    return {
      detail:
        typeof parsed?.detail === 'string'
          ? parsed.detail
          : 'The complete PDF could not be prepared.',
    };
  } catch {
    return {
      detail: 'The complete PDF could not be prepared.',
    };
  }
};

export type args = {
  postSubmittals: ApiType<RfiSubmittalsType.Submittals.Post>['args'];
  getAllSubmittals: ApiType<RfiSubmittalsType.Submittals.Get>['args'];
  getSubmittal: ApiType<RfiSubmittalsType.Submittals.GetSubmittal>['args'];
  patchSubmittal: ApiType<RfiSubmittalsType.Submittals.Patch>['args'];
  postSubmittalAttachments: ApiType<RfiSubmittalsType.Submittals.PostAttachments>['args'];
  deleteSubmittalAttachment: {
    params: { submittal_id: number; attachment_id: number };
  };

  postSubmittalsExportLog: ApiType<RfiSubmittalsType.Submittals.PostExportLog>['args'];
  deleteSubmittal: { params: { submittal_id: number } };
  postSubmittalExportPdf: ApiType<RfiSubmittalsType.Submittals.PostExportPdf>['args'];

  getSubmittalReturnAttachments:
    ApiType<RfiSubmittalsType.Submittals.GetSubmittalReturnAttachments>['args'];
  postSubmittalReturnAttachment:
    ApiType<RfiSubmittalsType.Submittals.PostSubmittalReturnAttachment>['args'];
  deleteSubmittalReturnAttachment:
    ApiType<RfiSubmittalsType.Submittals.DeleteSubmittalReturnAttachment>['args'];
};

export type responses = {
  postSubmittals: ApiType<RfiSubmittalsType.Submittals.Post>['response'];
  getAllSubmittals: ApiType<RfiSubmittalsType.Submittals.Get>['response'];
  getSubmittal: ApiType<RfiSubmittalsType.Submittals.GetSubmittal>['response'];
  patchSubmittal: ApiType<RfiSubmittalsType.Submittals.Patch>['response'];
  postSubmittalAttachments: ApiType<RfiSubmittalsType.Submittals.PostAttachments>['response'];
  deleteSubmittalAttachment: void;
  postSubmittalsExportLog: Blob;
  postSubmittalExportPdf: Blob;
  deleteSubmittal: void;

  getSubmittalReturnAttachments:
    ApiType<RfiSubmittalsType.Submittals.GetSubmittalReturnAttachments>['response'];
  postSubmittalReturnAttachment:
    ApiType<RfiSubmittalsType.Submittals.PostSubmittalReturnAttachment>['response'];
  deleteSubmittalReturnAttachment: void;
};

export const submittalsApi = createApi({
  reducerPath: 'submittalsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Submittals'],

  endpoints: (builder) => ({
    postSubmittals: builder.mutation<
      responses['postSubmittals'],
      args['postSubmittals']
    >({
      query: ({ body }) => ({
        url: '/rfi/submittals/',
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 201 && !result.isError,
      }),
      invalidatesTags: [{ type: 'Submittals' }],
    }),

    getAllSubmittals: builder.query<
      responses['getAllSubmittals'],
      args['getAllSubmittals']
    >({
      query: ({ query }) => ({
        url: '/rfi/submittals/',
        method: 'GET',
        params: query,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (result) => (result ? [{ type: 'Submittals' }] : []),
    }),

    getSubmittal: builder.query<
      responses['getSubmittal'],
      args['getSubmittal']
    >({
      query: ({ params }) => ({
        url: `/rfi/submittals/${params.submittal_id}/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _args) =>
        _result
          ? [
              { type: 'Submittals' },
              {
                type: 'Submittals',
                id: `submittal-${_args.params.submittal_id}`,
              },
            ]
          : [],
    }),

    patchSubmittal: builder.mutation<
      responses['patchSubmittal'],
      args['patchSubmittal']
    >({
      query: ({ body, params }) => ({
        url: `/rfi/submittals/${params.submittal_id}/`,
        method: 'PATCH',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'Submittals' },
        { type: 'Submittals', id: `submittal-${_arg.params.submittal_id}` },
        {
          type: 'Submittals',
          id: `submittal-return-attachments-${_arg.params.submittal_id}`,
        },
      ],
    }),

    postSubmittalAttachments: builder.mutation<
      responses['postSubmittalAttachments'],
      args['postSubmittalAttachments']
    >({
      query: ({ body, params }) => ({
        url: `/rfi/submittals/${params.submittal_id}/attachments/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 201 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'Submittals' },
        { type: 'Submittals', id: `submittal-${_arg.params.submittal_id}` },
      ],
    }),

    postSubmittalAttachmentPresign: builder.mutation<
      {
        presigned_urls: {
          filename: string;
          upload_url: string;
          file_url: string;
        }[];
      },
      {
        files: { filename: string; content_type: string; size?: number }[];
      }
    >({
      query: (body) => ({
        url: `/rfi/submittals/attachments/presign/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
    }),

    postSubmittalExportPdf: builder.mutation<
      responses['postSubmittalExportPdf'],
      args['postSubmittalExportPdf']
    >({
      query: ({ params, body }) => ({
        url: `/rfi/submittals/${params.submittal_id}/export/pdf/`,
        method: 'POST',
        body,
        responseHandler: handleSubmittalPdfResponse,
        validateStatus: (response) => response.status === 200,
      }),
    }),

    postSubmittalsExportLog: builder.mutation<
      responses['postSubmittalsExportLog'],
      args['postSubmittalsExportLog']
    >({
      query: ({ body }) => ({
        url: `/rfi/submittals/export-log/`,
        method: 'POST',
        body,
        responseHandler: (response) => response.blob(),
        validateStatus: (response) => response.status === 200,
      }),
    }),

    deleteSubmittal: builder.mutation<
      void,
      { params: { submittal_id: number } }
    >({
      query: ({ params }) => ({
        url: `/rfi/submittals/${params.submittal_id}/delete/`,
        method: 'DELETE',
        validateStatus: (response) => response.status === 204,
      }),
      invalidatesTags: [{ type: 'Submittals' }],
    }),

    deleteSubmittalAttachment: builder.mutation<
      { code: string; message: string },
      { params: { submittal_id: number; attachment_id: number } }
    >({
      query: ({ params }) => ({
        url: `/rfi/submittals/${params.submittal_id}/attachments/${params.attachment_id}/`,
        method: 'DELETE',
        validateStatus: (response, result) =>
          response.status === 200 && !result?.isError,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'Submittals' },
        { type: 'Submittals', id: `submittal-${_arg.params.submittal_id}` },
      ],
    }),

    getSubmittalReturnAttachments: builder.query<
      responses['getSubmittalReturnAttachments'],
      args['getSubmittalReturnAttachments']
    >({
      query: ({ params }) => ({
        url: `/rfi/submittals/${params.submittal_id}/return-attachments/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) => [
        { type: 'Submittals', id: `submittal-${_arg.params.submittal_id}` },
        {
          type: 'Submittals',
          id: `submittal-return-attachments-${_arg.params.submittal_id}`,
        },
      ],
    }),

    postSubmittalReturnAttachment: builder.mutation<
      responses['postSubmittalReturnAttachment'],
      args['postSubmittalReturnAttachment']
    >({
      query: ({ params, body }) => ({
        url: `/rfi/submittals/${params.submittal_id}/return-attachments/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          (response.status === 200 || response.status === 201) &&
          !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'Submittals' },
        { type: 'Submittals', id: `submittal-${_arg.params.submittal_id}` },
        {
          type: 'Submittals',
          id: `submittal-return-attachments-${_arg.params.submittal_id}`,
        },
      ],
    }),

    deleteSubmittalReturnAttachment: builder.mutation<
      responses['deleteSubmittalReturnAttachment'],
      args['deleteSubmittalReturnAttachment']
    >({
      query: ({ params }) => ({
        url: `/rfi/submittals/return-attachments/${params.return_id}/`,
        method: 'DELETE',
        validateStatus: (response) => response.status === 204,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'Submittals' },
        { type: 'Submittals', id: `submittal-${_arg.params.submittal_id}` },
        {
          type: 'Submittals',
          id: `submittal-return-attachments-${_arg.params.submittal_id}`,
        },
      ],
    }),
  }),
});

export const {
  usePostSubmittalsMutation,
  useGetAllSubmittalsQuery,
  useGetSubmittalQuery,
  usePatchSubmittalMutation,
  usePostSubmittalAttachmentsMutation,
  usePostSubmittalAttachmentPresignMutation,
  useDeleteSubmittalAttachmentMutation,
  usePostSubmittalsExportLogMutation,
  useDeleteSubmittalMutation,
  usePostSubmittalExportPdfMutation,
  useGetSubmittalReturnAttachmentsQuery,
  usePostSubmittalReturnAttachmentMutation,
  useDeleteSubmittalReturnAttachmentMutation,
} = submittalsApi;

export namespace SubmittalsApi {
  export const Api = submittalsApi;
  export type Args = args;
  export type Responses = responses;
}